import { useEffect, useState } from 'react';
import { getAttachmentUrl, updateAttachment } from '../services/customerInvoiceAttachmentService';
import { AttachmentTypeSelect } from './AttachmentTypeSelect';
import {
  ATTACHMENT_TYPE_CONFIG,
  formatFileSize,
  type CustomerInvoiceAttachmentView,
  type CustomerInvoiceAttachmentType,
} from '../types';

interface CustomerInvoiceAttachmentLightboxProps {
  attachment: CustomerInvoiceAttachmentView;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onDelete?: (attachment: CustomerInvoiceAttachmentView) => void;
  onUpdate?: (attachment: CustomerInvoiceAttachmentView) => void;
  showDeleteButton?: boolean;
  showEditButton?: boolean;
}

export function CustomerInvoiceAttachmentLightbox({
  attachment,
  onClose,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  onDelete,
  onUpdate,
  showDeleteButton = false,
  showEditButton = true,
}: CustomerInvoiceAttachmentLightboxProps) {
  const [contentUrl, setContentUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState(attachment);
  const [editType, setEditType] = useState<CustomerInvoiceAttachmentType>(attachment.attachmentType);
  const [editNotes, setEditNotes] = useState(attachment.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  // Update current attachment when prop changes (navigation)
  useEffect(() => {
    setCurrentAttachment(attachment);
    setEditType(attachment.attachmentType);
    setEditNotes(attachment.notes || '');
  }, [attachment]);

  const isImage = attachment.mimeType?.startsWith('image/');
  const isPdf = attachment.mimeType === 'application/pdf';

  useEffect(() => {
    let cancelled = false;

    const loadContent = async () => {
      setIsLoading(true);
      try {
        const url = await getAttachmentUrl(attachment.filePath);
        if (!cancelled) {
          setContentUrl(url);
        }
      } catch (error) {
        console.error('Error loading attachment:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadContent();

    return () => {
      cancelled = true;
    };
  }, [attachment.filePath]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          if (showDeleteConfirm) {
            setShowDeleteConfirm(false);
          } else if (showEditModal) {
            setShowEditModal(false);
          } else {
            onClose();
          }
          break;
        case 'ArrowLeft':
          if (hasPrevious && onPrevious) onPrevious();
          break;
        case 'ArrowRight':
          if (hasNext && onNext) onNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext, hasPrevious, hasNext, showDeleteConfirm, showEditModal]);

  // Prevent body scroll
  useEffect(() => {
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.body.style.overflow = '';
    };
  }, []);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete?.(attachment);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleDownload = () => {
    if (contentUrl) {
      window.open(contentUrl, '_blank');
    }
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      await updateAttachment({
        id: attachment.id,
        attachmentType: editType,
        notes: editNotes || undefined,
      });
      const updated = {
        ...currentAttachment,
        attachmentType: editType,
        notes: editNotes || null,
      };
      setCurrentAttachment(updated);
      onUpdate?.(updated);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating attachment:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const typeConfig = ATTACHMENT_TYPE_CONFIG[currentAttachment.attachmentType];

  return (
    <div className="fixed inset-0 z-50 bg-black/90" onClick={onClose}>
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 text-white z-10">
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{attachment.fileName}</p>
          <p className="text-sm text-white/70">{formatDate(attachment.createdAt)}</p>
        </div>
        <div className="flex items-center gap-2 ml-4">
          {/* Edit Button */}
          {showEditButton && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowEditModal(true);
              }}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Edit"
              title="Edit"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          )}
          {/* Download Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Download"
            title="Download"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
          {/* Delete Button */}
          {showDeleteButton && onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick();
              }}
              className="p-2 rounded-full hover:bg-white/10 transition-colors text-red-400 hover:text-red-300"
              aria-label="Delete"
              title="Delete"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Content Container */}
      <div
        className="absolute top-16 bottom-32 left-0 right-0 flex items-center justify-center px-16"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Previous Button */}
        {hasPrevious && onPrevious && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
            className="absolute left-4 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-10"
            aria-label="Previous"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center">
            <svg
              className="animate-spin h-12 w-12 text-white/50"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        ) : contentUrl ? (
          isImage ? (
            <img
              src={contentUrl}
              alt={attachment.fileName}
              className="max-w-full max-h-full object-contain"
            />
          ) : isPdf ? (
            <iframe
              src={contentUrl}
              className="w-full h-full bg-white rounded-lg"
              title="PDF Viewer"
            />
          ) : (
            <div className="text-white/50">Unsupported file type</div>
          )
        ) : (
          <div className="text-white/50">Failed to load content</div>
        )}

        {/* Next Button */}
        {hasNext && onNext && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-4 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-10"
            aria-label="Next"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Details Footer */}
      <div
        className="absolute bottom-0 left-0 right-0 p-4 bg-black/50 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-w-3xl mx-auto space-y-3">
          {/* Meta Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-white/50">Type</p>
              <p>{typeConfig.label}</p>
            </div>
            {currentAttachment.fileSizeBytes && (
              <div>
                <p className="text-white/50">Size</p>
                <p>{formatFileSize(currentAttachment.fileSizeBytes)}</p>
              </div>
            )}
            {currentAttachment.projectName && (
              <div>
                <p className="text-white/50">Project</p>
                <p>{currentAttachment.projectName}</p>
              </div>
            )}
            {currentAttachment.uploadedByName && (
              <div>
                <p className="text-white/50">Uploaded by</p>
                <p>{currentAttachment.uploadedByName}</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {currentAttachment.notes && (
            <div>
              <p className="text-white/50 text-sm">Notes</p>
              <p className="text-sm">{currentAttachment.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="absolute inset-0 z-[60] flex items-center justify-center bg-black/50"
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteConfirm(false);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 text-center mb-2">
                Delete Document?
              </h3>
              <p className="text-secondary-600 text-center text-sm mb-6">
                This will permanently delete this file. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border border-secondary-300 text-secondary-700 font-medium rounded-lg hover:bg-secondary-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div
          className="absolute inset-0 z-[60] flex items-center justify-center bg-black/50"
          onClick={(e) => {
            e.stopPropagation();
            setShowEditModal(false);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900">Edit Document</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 rounded-full hover:bg-secondary-100 transition-colors"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5 text-secondary-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <AttachmentTypeSelect
                value={editType}
                onChange={setEditType}
                disabled={isSaving}
              />
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  disabled={isSaving}
                  rows={3}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                  placeholder="Add notes about this document..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 border border-secondary-300 text-secondary-700 font-medium rounded-lg hover:bg-secondary-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
